<template>
  <div class="register-documents">
    <div class="form-container">
      <v-card class="section-card" elevation="0">
        <v-form ref="form" v-model="valid">
          <div class="form-header">
            <div class="step-circle">3</div>
            <h2 class="form-title">อัพโหลดเอกสาร</h2>
          </div>

          <p class="muted mb-4">
            กรุณาอัพโหลดเอกสารที่จำเป็นสำหรับการยืนยันตัวตน
            ข้อมูลทั้งหมดจะถูกเก็บรักษาอย่างปลอดภัย
          </p>

          <v-divider class="mb-4"></v-divider>

          <v-row>
            <!-- อัพโหลดสมุดบัญชี -->
            <v-col cols="12">
              <span class="detail-text"
                ><span class="req">*</span>สมุดบัญชี</span
              >
              <div class="upload-section">
                <div v-if="bankBookPreview" class="preview-wrapper">
                  <v-img
                    :src="bankBookPreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn icon small class="remove-btn" @click="removeBankBook">
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerBankBookInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดสมุดบัญชี</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="bankBookInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileChange('bankBookFile', 'bankBookPreview', 'bankBookInput', $event)"
                />
              </div>
            </v-col>

            <!-- อัพโหลดบัตรประจำตัว -->
            <v-col cols="12">
              <span class="detail-text"
                ><span class="req">*</span>บัตรประจำตัวประชาชน</span
              >
              <div class="upload-section">
                <div v-if="idCardPreview" class="preview-wrapper">
                  <v-img
                    :src="idCardPreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn icon small class="remove-btn" @click="removeIdCard">
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerIdCardInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดบัตรประจำตัวประชาชน</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="idCardInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileChange('idCardFile', 'idCardPreview', 'idCardInput', $event)"
                />
              </div>
            </v-col>

            <!-- เลือกประเภทยืนยันตัวตนเพิ่มเติม -->
            <v-col cols="12">
              <v-divider class="my-2"></v-divider>
              <p class="section-subtitle mt-4 mb-3">
                <span class="req">*</span>เลือกประเภทเอกสารยืนยันตัวตนเพิ่มเติม (เลือก 1 ตัวเลือก)
              </p>
              <div class="option-tabs">
                <button
                  type="button"
                  class="option-tab"
                  :class="{ active: verifyOption === '1' }"
                  @click="selectOption('1')"
                >
                  <v-icon small class="mr-1">mdi-store</v-icon>
                  ตัวเลือกที่ 1 — ใบพาณิชย์ + รูปถ่ายร้านค้า
                </button>
                <button
                  type="button"
                  class="option-tab"
                  :class="{ active: verifyOption === '2' }"
                  @click="selectOption('2')"
                >
                  <v-icon small class="mr-1">mdi-account-box</v-icon>
                  ตัวเลือกที่ 2 — รูปถ่ายผู้ขาย (ตรงกับบัตรประชาชน)
                </button>
              </div>
            </v-col>

            <!-- ตัวเลือกที่ 1: ใบพาณิชย์ + รูปถ่ายร้านค้า -->
            <template v-if="verifyOption === '1'">
              <v-col cols="12">
                <p class="option-desc">
                  ใบพาณิชย์ร้านค้า 1 รูป + รูปถ่ายร้านค้า 1 รูป
                </p>
              </v-col>
              <v-col cols="12" sm="6">
                <span class="detail-text"><span class="req">*</span>ใบพาณิชย์ร้านค้า</span>
                <div class="upload-section">
                  <div v-if="businessLicensePreview" class="preview-wrapper">
                    <v-img :src="businessLicensePreview" class="preview-image" contain max-height="200" />
                    <v-btn icon small class="remove-btn" @click="removeFile('businessLicenseFile', 'businessLicensePreview', 'businessLicenseInput')">
                      <v-icon color="white" size="20">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div v-else class="upload-placeholder" @click="$refs.businessLicenseInput.click()">
                    <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                    <span class="upload-text">คลิกเพื่ออัพโหลดใบพาณิชย์ร้านค้า</span>
                    <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                  </div>
                  <input
                    ref="businessLicenseInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileChange('businessLicenseFile', 'businessLicensePreview', 'businessLicenseInput', $event)"
                  />
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <span class="detail-text"><span class="req">*</span>รูปถ่ายร้านค้า</span>
                <div class="upload-section">
                  <div v-if="storePhotoPreview" class="preview-wrapper">
                    <v-img :src="storePhotoPreview" class="preview-image" contain max-height="200" />
                    <v-btn icon small class="remove-btn" @click="removeFile('storePhotoFile', 'storePhotoPreview', 'storePhotoInput')">
                      <v-icon color="white" size="20">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div v-else class="upload-placeholder" @click="$refs.storePhotoInput.click()">
                    <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                    <span class="upload-text">คลิกเพื่ออัพโหลดรูปถ่ายร้านค้า</span>
                    <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                  </div>
                  <input
                    ref="storePhotoInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileChange('storePhotoFile', 'storePhotoPreview', 'storePhotoInput', $event)"
                  />
                </div>
              </v-col>
            </template>

            <!-- ตัวเลือกที่ 2: รูปถ่ายครึ่งตัว + รูปถ่ายเต็มตัว -->
            <template v-if="verifyOption === '2'">
              <v-col cols="12">
                <p class="option-desc">
                  รูปถ่ายผู้ขายครึ่งตัว 1 รูป + รูปถ่ายเต็มตัว 1 รูป (ใบหน้าตรงกับบัตรประชาชน)
                </p>
              </v-col>
              <v-col cols="12" sm="6">
                <span class="detail-text"><span class="req">*</span>รูปถ่ายครึ่งตัว</span>
                <div class="upload-section">
                  <div v-if="halfBodyPhotoPreview" class="preview-wrapper">
                    <v-img :src="halfBodyPhotoPreview" class="preview-image" contain max-height="200" />
                    <v-btn icon small class="remove-btn" @click="removeFile('halfBodyPhotoFile', 'halfBodyPhotoPreview', 'halfBodyPhotoInput')">
                      <v-icon color="white" size="20">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div v-else class="upload-placeholder" @click="$refs.halfBodyPhotoInput.click()">
                    <v-icon size="48" color="#9ca3af">mdi-camera-account</v-icon>
                    <span class="upload-text">คลิกเพื่ออัพโหลดรูปถ่ายครึ่งตัว</span>
                    <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                  </div>
                  <input
                    ref="halfBodyPhotoInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileChange('halfBodyPhotoFile', 'halfBodyPhotoPreview', 'halfBodyPhotoInput', $event)"
                  />
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <span class="detail-text"><span class="req">*</span>รูปถ่ายเต็มตัว</span>
                <div class="upload-section">
                  <div v-if="fullBodyPhotoPreview" class="preview-wrapper">
                    <v-img :src="fullBodyPhotoPreview" class="preview-image" contain max-height="200" />
                    <v-btn icon small class="remove-btn" @click="removeFile('fullBodyPhotoFile', 'fullBodyPhotoPreview', 'fullBodyPhotoInput')">
                      <v-icon color="white" size="20">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div v-else class="upload-placeholder" @click="$refs.fullBodyPhotoInput.click()">
                    <v-icon size="48" color="#9ca3af">mdi-human-handsup</v-icon>
                    <span class="upload-text">คลิกเพื่ออัพโหลดรูปถ่ายเต็มตัว</span>
                    <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                  </div>
                  <input
                    ref="fullBodyPhotoInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileChange('fullBodyPhotoFile', 'fullBodyPhotoPreview', 'fullBodyPhotoInput', $event)"
                  />
                </div>
              </v-col>
            </template>

            <!-- ปุ่ม -->
            <v-col cols="12" class="mt-3 d-flex justify-space-between">
              <v-btn class="btn-secondary" height="40" @click="handleBack">
                ย้อนกลับ
              </v-btn>
              <v-btn class="btn-primary" height="40" @click="handleNext">
                ถัดไป
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </div>
  </div>
</template>

<script>
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "RegisterDocuments",
  props: {
    value: {
      type: Object,
      default: () => ({
        bankBookFile: null,
        idCardFile: null,
        verifyOption: null,
        businessLicenseFile: null,
        storePhotoFile: null,
        halfBodyPhotoFile: null,
        fullBodyPhotoFile: null,
      }),
    },
  },

  data() {
    return {
      valid: false,
      form: {
        bankBookFile: this.value.bankBookFile || null,
        idCardFile: this.value.idCardFile || null,
        verifyOption: this.value.verifyOption || null,
        businessLicenseFile: this.value.businessLicenseFile || null,
        storePhotoFile: this.value.storePhotoFile || null,
        halfBodyPhotoFile: this.value.halfBodyPhotoFile || null,
        fullBodyPhotoFile: this.value.fullBodyPhotoFile || null,
      },
      bankBookPreview: null,
      idCardPreview: null,
      businessLicensePreview: null,
      storePhotoPreview: null,
      halfBodyPhotoPreview: null,
      fullBodyPhotoPreview: null,
    };
  },

  computed: {
    verifyOption: {
      get() { return this.form.verifyOption; },
      set(v) { this.form.verifyOption = v; },
    },
  },

  methods: {
    handleBack() {
      this.$emit("input", { ...this.form });
      this.$emit("back");
    },
    handleNext() {
      if (!this.form.bankBookFile) {
        swalAlert.Error("กรุณาอัพโหลดสมุดบัญชี");
        return;
      }
      if (!this.form.idCardFile) {
        swalAlert.Error("กรุณาอัพโหลดบัตรประจำตัวประชาชน");
        return;
      }
      if (!this.form.verifyOption) {
        swalAlert.Error("กรุณาเลือกประเภทเอกสารยืนยันตัวตนเพิ่มเติม");
        return;
      }
      if (this.form.verifyOption === '1') {
        if (!this.form.businessLicenseFile) {
          swalAlert.Error("กรุณาอัพโหลดใบพาณิชย์ร้านค้า");
          return;
        }
        if (!this.form.storePhotoFile) {
          swalAlert.Error("กรุณาอัพโหลดรูปถ่ายร้านค้า");
          return;
        }
      }
      if (this.form.verifyOption === '2') {
        if (!this.form.halfBodyPhotoFile) {
          swalAlert.Error("กรุณาอัพโหลดรูปถ่ายครึ่งตัว");
          return;
        }
        if (!this.form.fullBodyPhotoFile) {
          swalAlert.Error("กรุณาอัพโหลดรูปถ่ายเต็มตัว");
          return;
        }
      }
      this.$emit("next", { ...this.form });
    },

    selectOption(opt) {
      if (this.form.verifyOption === opt) return;
      this.form.verifyOption = opt;
      // ล้างไฟล์ option เก่าเมื่อสลับ
      this.clearOptionFiles();
    },
    clearOptionFiles() {
      const fields = [
        ['businessLicenseFile', 'businessLicensePreview', 'businessLicenseInput'],
        ['storePhotoFile', 'storePhotoPreview', 'storePhotoInput'],
        ['halfBodyPhotoFile', 'halfBodyPhotoPreview', 'halfBodyPhotoInput'],
        ['fullBodyPhotoFile', 'fullBodyPhotoPreview', 'fullBodyPhotoInput'],
      ];
      for (const [fileField, previewField, refName] of fields) {
        this.revokePreview(previewField);
        this.form[fileField] = null;
        if (this.$refs[refName]) this.$refs[refName].value = '';
      }
    },

    triggerBankBookInput() {
      this.$refs.bankBookInput?.click();
    },
    triggerIdCardInput() {
      this.$refs.idCardInput?.click();
    },

    handleFileChange(fileField, previewField, refName, event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      this.revokePreview(previewField);
      this.form[fileField] = file;
      this[previewField] = URL.createObjectURL(file);
      event.target.value = "";
    },

    removeFile(fileField, previewField, refName) {
      this.revokePreview(previewField);
      this.form[fileField] = null;
      if (this.$refs[refName]) this.$refs[refName].value = '';
    },
    removeBankBook() {
      this.removeFile('bankBookFile', 'bankBookPreview', 'bankBookInput');
    },
    removeIdCard() {
      this.removeFile('idCardFile', 'idCardPreview', 'idCardInput');
    },

    revokePreview(previewField) {
      if (this[previewField]?.startsWith?.("blob:")) {
        URL.revokeObjectURL(this[previewField]);
      }
      this[previewField] = null;
    },

    initPreviewFromFile(fileField, previewField) {
      const file = this.form[fileField];
      if (file instanceof File) {
        this.revokePreview(previewField);
        this[previewField] = URL.createObjectURL(file);
      }
    },
  },

  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(v) {
        if (!v) return;
        this.form = { ...this.form, ...v };
        const pairs = [
          ['bankBookFile', 'bankBookPreview'],
          ['idCardFile', 'idCardPreview'],
          ['businessLicenseFile', 'businessLicensePreview'],
          ['storePhotoFile', 'storePhotoPreview'],
          ['halfBodyPhotoFile', 'halfBodyPhotoPreview'],
          ['fullBodyPhotoFile', 'fullBodyPhotoPreview'],
        ];
        for (const [f, p] of pairs) {
          this.initPreviewFromFile(f, p);
        }
      },
    },
  },

  beforeDestroy() {
    const previews = [
      'bankBookPreview', 'idCardPreview',
      'businessLicensePreview', 'storePhotoPreview',
      'halfBodyPhotoPreview', 'fullBodyPhotoPreview',
    ];
    for (const p of previews) {
      if (this[p]?.startsWith?.("blob:")) URL.revokeObjectURL(this[p]);
    }
  },
};
</script>

<style scoped>
.register-documents {
  padding-bottom: 90px;
}
.form-container {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  padding: 0 16px;
}
.form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #333;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
}
.form-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}
.req {
  color: #e11d48;
  margin-right: 4px;
}
.section-card {
  padding: 24px;
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(2, 6, 23, 0.06);
}
.section-card :deep(p.muted) {
  text-align: left !important;
  line-height: 1.6;
  color: #4b5563;
  margin-bottom: 16px;
}
.detail-text {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #1f2a39;
  line-height: 1.4;
  margin-bottom: 8px;
}
.section-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #1f2a39;
  margin: 0;
}
.option-tabs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.option-tab {
  flex: 1 1 200px;
  padding: 12px 16px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  color: #374151;
  font-size: 13px;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
}
.option-tab:hover {
  border-color: #F4811F;
  background: #fff5f0;
  color: #c2550a;
}
.option-tab.active {
  border-color: #F4811F;
  background: #fff5f0;
  color: #c2550a;
  font-weight: 600;
}
.option-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 4px 0 0 0;
  font-style: italic;
}
.upload-section {
  margin-bottom: 16px;
}
.upload-placeholder {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;
}
.upload-placeholder:hover {
  border-color: #F4811F;
  background: #fff5f0;
}
.upload-text {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-top: 8px;
  word-break: break-word;
  overflow-wrap: break-word;
}
.upload-hint {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  word-break: break-word;
  overflow-wrap: break-word;
}
.preview-wrapper {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
}
.preview-image {
  width: 100%;
  display: block;
}
.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6) !important;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  min-width: 32px;
}
.remove-btn:hover {
  background: rgba(0, 0, 0, 0.8) !important;
}
.btn-primary {
  background: #F4811F !important;
  color: #fff !important;
  border-radius: 8px !important;
}
.btn-secondary {
  background: #e5e7eb !important;
  color: #111827 !important;
  border-radius: 8px !important;
}
@media (max-width: 599px) {
  .register-documents { padding-bottom: 60px; }
  .section-card { padding: 16px 12px; }
  .btn-primary, .btn-secondary { min-height: 44px !important; }
  .upload-placeholder { padding: 20px 12px; }
  .upload-text { font-size: 12px; }
  .option-tab { flex: 1 1 100%; }
}
</style>
